export type OrderKey = "market" | "limit" | "stop" | "stop-limit";
export type MarketMode = "calm" | "gap";
export type Tone = "primary" | "accent" | "muted";
export type Placement = "top" | "bottom" | "left" | "right";

export interface Pt {
  x: number;
  y: number;
}

export interface Cross {
  x: number;
  y: number;
  vertical: boolean;
}

/* ------------------------------------------------------------------ *
 * Scale: y = 49 is the live price (1.08467). One y-unit ≈ 1.7 pips.
 * Coordinates are percentages of the plot area (x: left→right, y: top→bottom).
 * ------------------------------------------------------------------ */
export const LIVE_Y = 49;
export const LIVE_PRICE = 1.08467;
export const PRICE_PER_Y = 0.00017;
export const PIP_PER_Y = 1.7;

export const priceAtY = (y: number): number => LIVE_PRICE + (LIVE_Y - y) * PRICE_PER_Y;
export const pipsBetween = (a: number, b: number): number => Math.abs(b - a) * PIP_PER_Y;

/* Illustrative EUR/USD spread (in pips) used to price a market entry. */
export const SPREAD_PIPS = 1.2;

/* Draggable sell-stop range, and how far below it the limit sits. */
export const STOP_MIN = 56;
export const STOP_MAX = 88;
export const STOP_DEFAULT = 80;
export const LIMIT_GAP = 8;
export const LIMIT_MIN_GAP = 1.5;
export const LIMIT_MAX = 94;

export const STROKE = 0.9;

/* Calm market — the descent is smooth, no gap. */
export const CALM_PATH: Pt[] = [
  { x: 4, y: 49 },
  { x: 10, y: 45 },
  { x: 16, y: 40 },
  { x: 21, y: 34 },
  { x: 28, y: 30 },
  { x: 34, y: 24 },
  { x: 40, y: 20 },
  { x: 46, y: 25 },
  { x: 52, y: 34 },
  { x: 58, y: 45 },
  { x: 64, y: 55 },
  { x: 70, y: 66 },
  { x: 75, y: 62 },
  { x: 80, y: 68 },
  { x: 84, y: 74 },
  { x: 88, y: 78 },
  { x: 92, y: 82 },
  { x: 96, y: 86 },
  { x: 100, y: 89 },
];

/* Gap market — a vertical jump from y=80 to y=90 at x=86. */
export const GAP_PATH: Pt[] = [
  { x: 4, y: 49 },
  { x: 10, y: 45 },
  { x: 16, y: 40 },
  { x: 21, y: 34 },
  { x: 28, y: 30 },
  { x: 34, y: 24 },
  { x: 40, y: 20 },
  { x: 46, y: 25 },
  { x: 52, y: 34 },
  { x: 58, y: 45 },
  { x: 64, y: 55 },
  { x: 70, y: 66 },
  { x: 75, y: 62 },
  { x: 80, y: 68 },
  { x: 84, y: 74 },
  { x: 86, y: 80 },
  { x: 86, y: 90 },
  { x: 91, y: 88 },
  { x: 96, y: 86 },
  { x: 100, y: 84 },
];

export const GAP_INFO = { x: 86, yTop: 80, yBottom: 90 };

export function getPath(mode: MarketMode): Pt[] {
  return mode === "gap" ? GAP_PATH : CALM_PATH;
}

/* Linear interpolation of y at a given x. Vertical segments return their lower point. */
export function yAtX(path: Pt[], x: number): number {
  if (x <= path[0].x) return path[0].y;
  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i];
    const b = path[i + 1];
    if (x >= a.x && x <= b.x) {
      if (b.x === a.x) return b.y;
      return a.y + ((x - a.x) / (b.x - a.x)) * (b.y - a.y);
    }
  }
  return path[path.length - 1].y;
}

/* First time the path falls through a price level (y increasing past `level`). */
export function firstCrossDown(path: Pt[], level: number, fromX = 0): Cross | null {
  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i];
    const b = path[i + 1];
    if (b.x < fromX) continue;
    const da = a.y - level;
    const db = b.y - level;
    const vertical = Math.abs(b.x - a.x) < 0.35;
    if (da < 0 && db >= 0) {
      const x = vertical ? a.x : a.x + ((level - a.y) / (b.y - a.y)) * (b.x - a.x);
      if (vertical || x >= fromX) return { x, y: level, vertical };
    } else if (da === 0 && db > 0 && a.x >= fromX) {
      return { x: a.x, y: level, vertical };
    }
  }
  return null;
}

export interface StopLimitResult {
  /** Where the stop triggers the order into life. */
  trigger: Cross | null;
  /** Did the resting limit actually get touched (filled) — or did a gap skip it? */
  filled: boolean;
  /** Trigger / fill point to draw on the chart. */
  fill: { x: number; y: number } | null;
}

/**
 * Resolve a sell stop-limit order against a price path.
 * The stop fires at `stopY`, then rests a sell limit at `limitY` (further down the
 * chart). The limit only fills if the path physically reaches it — a vertical gap
 * that jumps from above the limit to below it leaves the order unfilled.
 */
export function resolveStopLimit(path: Pt[], stopY: number, limitY: number): StopLimitResult {
  const trigger = firstCrossDown(path, stopY);
  if (!trigger) return { trigger: null, filled: false, fill: null };

  const startX = trigger.x;

  for (let i = 0; i < path.length - 1; i += 1) {
    const a = path[i];
    const b = path[i + 1];
    if (b.x < startX - 0.01) continue;

    const vertical = Math.abs(b.x - a.x) < 0.35;
    const da = a.y - limitY;
    const db = b.y - limitY;

    if (vertical) {
      if (da < 0 && db >= 0) {
        if (db === 0) return { trigger, filled: true, fill: { x: b.x, y: limitY } };
        return { trigger, filled: false, fill: { x: b.x, y: limitY } };
      }
    } else if (da < 0 && db >= 0) {
      const x = a.x + ((limitY - a.y) / (b.y - a.y)) * (b.x - a.x);
      if (x >= startX - 0.01) return { trigger, filled: true, fill: { x, y: limitY } };
    } else if (da === 0 && db > 0 && a.x >= startX - 0.01) {
      return { trigger, filled: true, fill: { x: a.x, y: limitY } };
    }
  }

  return { trigger, filled: false, fill: { x: startX, y: limitY } };
}

export const areaPolygon = (path: Pt[]): string =>
  `${path.map((p) => `${p.x}% ${p.y}%`).join(", ")}, 100% 100%, 0% 100%`;

export const strokePolygon = (path: Pt[]): string =>
  [
    ...path.map((p) => `${p.x}% ${p.y - STROKE}%`),
    ...[...path].reverse().map((p) => `${p.x}% ${p.y + STROKE}%`),
  ].join(", ");

export interface ScenarioMeta {
  key: OrderKey;
  label: string;
  icon: string;
  levels: string[];
}

export const SCENARIOS: ScenarioMeta[] = [
  { key: "market", label: "Market", icon: "ri-rocket-2-line", levels: ["live"] },
  { key: "limit", label: "Limit", icon: "ri-filter-3-line", levels: ["buy-limit", "sell-limit"] },
  {
    key: "stop",
    label: "Stop",
    icon: "ri-alarm-warning-line",
    levels: ["buy-stop", "sell-stop", "live"],
  },
  {
    key: "stop-limit",
    label: "Stop-Limit",
    icon: "ri-shield-keyhole-line",
    levels: ["sell-stop", "sl-cap"],
  },
];

export const ORDER_KEYS: OrderKey[] = ["market", "limit", "stop", "stop-limit"];